import Checkbox from '@alexis/components/Checkbox';
import { clsx } from '@alexis/helpers/clsx';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { translateNumber, translateText } from '@wego/alexis/helpers/translation';

import HotelAmenityIcon from '@components/hotels/HotelAmenityIcon';
import LogSlider from '@components/LogSlider';
import Price from '@components/Price';
import Slider from '@components/Slider';

import { NEW_RATING_SCALE } from '@constants/hotels';

import { GlobalContext } from '@context/GlobalContext';

import { notUndefinedAndNull } from '@helpers/array';
import { getReviewerGroupFilterTranslation } from '@helpers/review';

import BusinessIcon from '@icons/business.svg';
import CoupleIcon from '@icons/couple.svg';
import FamilyIcon from '@icons/family.svg';
import SoloIcon from '@icons/solo.svg';
import StarIcon from '@icons/star_rounded.svg';
import WorldIcon from '@icons/world.svg';

import {
  getCurrencyState,
  getExchangeRateState,
  getHotelMetaSearchState,
  getIsRtlState,
  getLocaleState,
  getPriceOptionsState,
  getTranslationsState,
} from '@redux/selectors';

import { Currency } from '@wegoTypes/currency';
import { HotelMetaSearch } from '@wegoTypes/hotels/hotelMetaSearch';
import { Translations, TranslationsKey } from '@wegoTypes/translations';

import styles from './HotelFilter.module.scss';

interface HotelFilterProps {
  className?: string;
  filteredHotelsCount: number;
  onClearFilter(): void;
  onPopularFilterClick(filterName: string): void;

  todayDealsCount: number;
  isTodayDeals: boolean;
  onIsTodayDealsToggle(isSelected: boolean): void;
  minUsdPrice: number | undefined;
  maxUsdPrice: number | undefined;
  selectedUsdPrices: Array<number>;
  onSelectedUsdPricesChanged(usdPrices: Array<number>): void;

  onIsIncludingTaxChanged(isIncludingTax: boolean): void;
  onShowTotalPriceChanged(showTotalPriceChanged: boolean): void;

  reviewScoresRange: Array<number>;
  selectedReviewScores: Array<number>;
  onSelectedReviewScoresChanged(reviewScores: Array<number>): void;

  selectedAmenityIds: Array<number>;
  onAmenityToggle(amenityId: number, isSelected: boolean): void;

  selectedBookingOptions: Array<string>;
  onBookingOptionToggle(bookingOption: string, isSelected: boolean): void;

  selectedBrandIds: Array<number>;
  onBrandToggle(brandId: number, isSelected: boolean): void;

  selectedChainIds: Array<number>;
  onChainToggle(chainId: number, isSelected: boolean): void;

  selectedDistrictIds: Array<number>;
  onDistrictToggle(districtId: number, isSelected: boolean): void;

  selectedPropertyTypeIds: Array<number>;
  onPropertyTypeToggle(propertyTypeId: number, isSelected: boolean): void;

  selectedRateAmenityIds: Array<number>;
  onRateAmenityToggle(rateAmenityId: number, isSelected: boolean): void;

  selectedReviewerGroups: Array<string>;
  onReviewerGroupToggle(reviewerGroup: string, isSelected: boolean): void;

  selectedStarIds: Array<number>;
  onStarToggle(starId: number, isSelected: boolean): void;

  selectedRateProviderCodes: Array<string>;
  onRateProviderToggle(rateProviderCode: string, isSelected: boolean): void;
}

const HotelFilter: React.FC<HotelFilterProps> = ({
  className,

  filteredHotelsCount,
  onClearFilter,
  onPopularFilterClick,
  todayDealsCount,
  isTodayDeals,
  onIsTodayDealsToggle,
  minUsdPrice,
  maxUsdPrice,
  selectedUsdPrices,
  onSelectedUsdPricesChanged,

  onIsIncludingTaxChanged,
  onShowTotalPriceChanged,

  reviewScoresRange,
  selectedReviewScores,
  onSelectedReviewScoresChanged,

  selectedAmenityIds,
  onAmenityToggle,
  selectedBookingOptions,
  onBookingOptionToggle,
  selectedBrandIds,
  onBrandToggle,
  selectedChainIds,
  onChainToggle,
  selectedDistrictIds,
  onDistrictToggle,
  selectedPropertyTypeIds,
  onPropertyTypeToggle,
  selectedRateAmenityIds,
  onRateAmenityToggle,
  selectedReviewerGroups,
  onReviewerGroupToggle,

  selectedStarIds,
  onStarToggle,
  selectedRateProviderCodes,
  onRateProviderToggle,
}): JSX.Element => {
  const currency: Currency = useSelector(getCurrencyState);
  const exchangeRate: ExchangeRate = useSelector(getExchangeRateState);
  const hotelMetaSearch: HotelMetaSearch = useSelector(getHotelMetaSearchState);
  const isRtl: boolean = useSelector(getIsRtlState);
  const locale: string = useSelector(getLocaleState);
  const priceOptions: PriceOptions = useSelector(getPriceOptionsState);
  const translations: Translations = useSelector(getTranslationsState);

  const { userCentricCoreConfigs } = useContext(GlobalContext);
  const {
    amenitiesFilter: amenities,
    bookingOptionsFilter: bookingOptions,
    brandsFilter: brands,
    chainsFilter: chains,
    districtsFilter: districts,
    propertyTypesFilter: propertyTypes,
    rateAmenitiesFilter: rateAmenities,
    reviewerGroupsFilter: reviewerGroups,
    starsFilter: stars,
    rateProviders,
  } = hotelMetaSearch;

  const [isPopularFilterExpanded, setIsPopularFilterExpanded] = useState(true);

  const [isAmenitiesFilterExpanded, setIsAmenitiesFilterExpanded] = useState(true);
  const [isBookingOptionsFilterExpanded, setIsBookingOptionsFilterExpanded] = useState(true);
  const [isBrandsFilterExpanded, setIsBrandsFilterExpanded] = useState(false);
  const [isChainsFilterExpanded, setIsChainsFilterExpanded] = useState(false);
  const [isDistrictsFilterExpanded, setIsDistrictsFilterExpanded] = useState(true);
  const [isPriceFilterExpanded, setIsPriceFilterExpanded] = useState(true);
  const [isPropertyTypesFilterExpanded, setIsPropertyTypesFilterExpanded] = useState(true);
  const [isRateAmenitiesFilterExpanded, setIsRateAmenitiesFilterExpanded] = useState(true);
  const [isReviewerGroupsFilterExpanded, setIsReviewerGroupsFilterExpanded] = useState(true);
  const [isReviewScoreFilterExpanded, setIsReviewScoreFilterExpanded] = useState(true);
  const [isStarsFilterExpanded, setIsStarsFilterExpanded] = useState(true);
  const [isBookingSitesFilterExpanded, setIsBookingSitesFilterExpanded] = useState(true);
  const [newSelectedUsdPrices, setNewSelectedUsdPrices] =
    useState<Array<number>>(selectedUsdPrices);
  const [isPriceTypeDropdownExpanded, setIsPriceTypeDropdownExpanded] = useState(false);
  const [selectedReviewScoreSliderMark, setSelectedReviewScoreSliderMark] = useState<Array<number>>(
    [],
  );

  const isNewRatingScale =
    userCentricCoreConfigs.find((coreConfig) => coreConfig.key === NEW_RATING_SCALE)?.value === '1';

  const hotelsCount =
    hotelMetaSearch.hotelViewModels.length + hotelMetaSearch.sponsoredAdHotelViewModels.length;

  const reviewScoreSliderMarks = isNewRatingScale
    ? [0, 16, 33, 50, 67, 83, 100]
    : [0, 20, 40, 60, 80, 100];
  const reviewScoreSliderMinimumDistance = isNewRatingScale ? 16 : 20;

  const popularFilters = useMemo<
    Array<{ id: number; name: string; count: number; type: 'rateAmenity' | 'todayDeals' }>
  >(() => {
    const todayDealsFilter: Array<{
      id: number;
      name: string;
      count: number;
      type: 'rateAmenity' | 'todayDeals';
    }> =
      todayDealsCount > 0
        ? [
            {
              id: 0,
              name: `${translations.today_deal} (${translateNumber(
                todayDealsCount,
                locale === 'fa',
              )})`,
              count: todayDealsCount,
              type: 'todayDeals',
            },
          ]
        : [];

    const popularRateAmenityFilters = rateAmenities
      .filter((rateAmenity) => rateAmenity.id === 1 || rateAmenity.id === 2)
      .map<{ id: number; name: string; count: number; type: 'rateAmenity' }>((rateAmenity) => {
        return { ...rateAmenity, type: 'rateAmenity' };
      });

    return [...todayDealsFilter, ...popularRateAmenityFilters];
  }, [rateAmenities, todayDealsCount, translations]);

  const bookingSites = useMemo<Array<{ code: string; name: string }>>(() => {
    return rateProviders
      .filter((rateProvider) => rateProvider.type === 'OTA')
      .map<{ code: string; name: string }>((rateProvider) => {
        return { code: rateProvider.code, name: rateProvider.name };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rateProviders]);

  const priceSliderMinimumDistance = useMemo<number>(() => {
    if (!!minUsdPrice && !!maxUsdPrice) {
      const priceRange = maxUsdPrice - minUsdPrice;

      if (priceRange > 1000) {
        return 100;
      }

      if (priceRange < 1001) {
        return 10;
      }

      if (priceRange < 101) {
        return 1;
      }
    }
    return 0;
  }, [minUsdPrice, maxUsdPrice]);

  const reviewScores = useMemo<Array<number>>(() => {
    return hotelMetaSearch.hotelViewModels
      .map((hotelViewModel) => {
        const allReviewerGroup = hotelViewModel.hotel.reviews[0];

        if (!!allReviewerGroup) {
          return allReviewerGroup.score;
        }
      })
      .filter(notUndefinedAndNull);
  }, [hotelMetaSearch.hotelViewModels]);

  useEffect(() => {
    setNewSelectedUsdPrices(selectedUsdPrices);
  }, [selectedUsdPrices]);

  useEffect(() => {
    setSelectedReviewScoreSliderMark(
      selectedReviewScores.map(
        (selectedReviewScore) =>
          reviewScoreSliderMarks[reviewScoresRange.indexOf(selectedReviewScore)],
      ),
    );
  }, [selectedReviewScores]);

  // Set price type dropdown expanded to false if body is clicked
  useEffect(() => {
    if (isPriceTypeDropdownExpanded) {
      const handleBodyOnClick = (): void => {
        setIsPriceTypeDropdownExpanded(false);
      };

      const timerId = setTimeout(() => {
        document.body.addEventListener('click', handleBodyOnClick);
      }, 0);

      return () => {
        clearTimeout(timerId);
        document.body.removeEventListener('click', handleBodyOnClick);
      };
    }
  }, [isPriceTypeDropdownExpanded]);

  const handleAmenityClick = useCallback<(amenityId: number) => void>(
    (amenityId: number) => {
      const isSelected: boolean = !selectedAmenityIds.includes(amenityId);

      onAmenityToggle(amenityId, isSelected);
    },
    [selectedAmenityIds, onAmenityToggle],
  );

  const handleBookingOptionClick = useCallback<(bookingOption: string) => void>(
    (bookingOption: string) => {
      bookingOption =
        bookingOption === 'BOW' ? 'wego' : bookingOption === 'HOTEL_WEBSITE' ? 'hotels' : 'ota';

      const isSelected: boolean = !selectedBookingOptions.includes(bookingOption);

      onBookingOptionToggle(bookingOption, isSelected);
    },
    [selectedBookingOptions, onBookingOptionToggle],
  );

  const handleBrandClick = useCallback<(brandId: number) => void>(
    (brandId: number) => {
      const isSelected: boolean = !selectedBrandIds.includes(brandId);

      onBrandToggle(brandId, isSelected);
    },
    [selectedBrandIds, onBrandToggle],
  );

  const handleChainClick = useCallback<(chainId: number) => void>(
    (chainId: number) => {
      const isSelected: boolean = !selectedChainIds.includes(chainId);

      onChainToggle(chainId, isSelected);
    },
    [selectedChainIds, onChainToggle],
  );

  const handleDistrictClick = useCallback<(districtId: number) => void>(
    (districtId: number) => {
      const isSelected: boolean = !selectedDistrictIds.includes(districtId);

      onDistrictToggle(districtId, isSelected);
    },
    [selectedDistrictIds, onDistrictToggle],
  );

  const handlePriceSliderChange = useCallback<(values: Array<number>, activeThumb: number) => void>(
    (values: Array<number>, activeThumb: number) => {
      if (values.length === 2) {
        if (values[1] - values[0] < priceSliderMinimumDistance) {
          if (activeThumb === 0) {
            const clamped = Math.min(values[0], maxUsdPrice! - priceSliderMinimumDistance);
            setNewSelectedUsdPrices([clamped, clamped + priceSliderMinimumDistance]);
          } else {
            const clamped = Math.max(values[1], minUsdPrice! + priceSliderMinimumDistance);
            setNewSelectedUsdPrices([clamped - priceSliderMinimumDistance, clamped]);
          }
        } else {
          setNewSelectedUsdPrices(values);
        }
      }
    },
    [priceSliderMinimumDistance, minUsdPrice, maxUsdPrice],
  );

  const handlePriceSliderChangeCommitted = useCallback<
    (values: Array<number>, activeThumb: number) => void
  >(
    (values: Array<number>, activeThumb: number) => {
      if (values.length === 2) {
        if (values[1] - values[0] < priceSliderMinimumDistance) {
          if (activeThumb === 0) {
            const clamped = Math.min(values[0], maxUsdPrice! - priceSliderMinimumDistance);
            onSelectedUsdPricesChanged([clamped, clamped + priceSliderMinimumDistance]);
          } else {
            const clamped = Math.max(values[1], minUsdPrice! + priceSliderMinimumDistance);
            onSelectedUsdPricesChanged([clamped - priceSliderMinimumDistance, clamped]);
          }
        } else {
          onSelectedUsdPricesChanged(values);
        }
      }
    },
    [onSelectedUsdPricesChanged, priceSliderMinimumDistance],
  );

  const handlePriceTypeChange = useCallback<(showTotalPrice: boolean) => void>(
    (showTotalPrice: boolean) => {
      onShowTotalPriceChanged(showTotalPrice);

      setIsPriceTypeDropdownExpanded(false);
    },
    [onShowTotalPriceChanged],
  );

  const handlePropertyTypeClick = useCallback<(propertyTypeId: number) => void>(
    (propertyTypeId: number) => {
      const isSelected: boolean = !selectedPropertyTypeIds.includes(propertyTypeId);

      onPropertyTypeToggle(propertyTypeId, isSelected);
    },
    [selectedPropertyTypeIds, onPropertyTypeToggle],
  );

  const handleRateAmenityClick = useCallback<(rateAmenityId: number) => void>(
    (rateAmenityId: number) => {
      const isSelected: boolean = !selectedRateAmenityIds.includes(rateAmenityId);

      onRateAmenityToggle(rateAmenityId, isSelected);
    },
    [selectedRateAmenityIds, onRateAmenityToggle],
  );

  const handleReviewerGroupClick = useCallback<(reviewerGroup: string) => void>(
    (reviewerGroup: string) => {
      const isSelected: boolean = !selectedReviewerGroups.includes(reviewerGroup);

      onReviewerGroupToggle(reviewerGroup, isSelected);
    },
    [selectedReviewerGroups, onReviewerGroupToggle],
  );

  const handleReviewScoreSliderChange = useCallback<
    (values: Array<number>, activeThumb: number) => void
  >((values: Array<number>, activeThumb: number) => {
    if (values.length === 2) {
      if (values[1] - values[0] < reviewScoreSliderMinimumDistance) {
        if (activeThumb === 0) {
          const clamped = Math.min(values[0], 100 - reviewScoreSliderMinimumDistance);
          setSelectedReviewScoreSliderMark([clamped, clamped + reviewScoreSliderMinimumDistance]);
        } else {
          const clamped = Math.max(values[1], reviewScoreSliderMinimumDistance);
          setSelectedReviewScoreSliderMark([clamped - reviewScoreSliderMinimumDistance, clamped]);
        }
      } else {
        setSelectedReviewScoreSliderMark(values);
      }
    }
  }, []);

  const getReviewScoresRangeValue = useCallback<(reviewScoreSliderMark: number) => number>(
    (reviewScoreSliderMark: number) => {
      const reviewScoreSliderMarksIndex = reviewScoreSliderMarks.indexOf(reviewScoreSliderMark);

      return reviewScoresRange[
        reviewScoreSliderMarksIndex !== -1 ? reviewScoreSliderMarksIndex : 0
      ];
    },
    [reviewScoresRange],
  );

  const handleReviewScoreSliderChangeCommitted = useCallback<
    (values: Array<number>, activeThumb: number) => void
  >(
    (values: Array<number>, activeThumb: number) => {
      if (values.length === 2) {
        if (values[1] - values[0] < reviewScoreSliderMinimumDistance) {
          if (activeThumb === 0) {
            const clamped = Math.min(values[0], 100 - reviewScoreSliderMinimumDistance);
            onSelectedReviewScoresChanged([
              getReviewScoresRangeValue(clamped),
              getReviewScoresRangeValue(clamped + reviewScoreSliderMinimumDistance),
            ]);
          } else {
            const clamped = Math.max(values[1], reviewScoreSliderMinimumDistance);
            onSelectedReviewScoresChanged([
              getReviewScoresRangeValue(clamped - reviewScoreSliderMinimumDistance),
              getReviewScoresRangeValue(clamped),
            ]);
          }
        } else {
          onSelectedReviewScoresChanged(values.map((value) => getReviewScoresRangeValue(value)));
        }
      }
    },
    [onSelectedReviewScoresChanged, getReviewScoresRangeValue],
  );

  const handleStarClick = useCallback<(starId: number) => void>(
    (starId: number) => {
      const isSelected: boolean = !selectedStarIds.includes(starId);

      onStarToggle(starId, isSelected);
    },
    [selectedStarIds, onStarToggle],
  );

  const handleRateProviderClick = useCallback<(rateProviderCode: string) => void>(
    (rateProviderCode: string) => {
      const isSelected: boolean = !selectedRateProviderCodes.includes(rateProviderCode);

      onRateProviderToggle(rateProviderCode, isSelected);
    },
    [selectedRateProviderCodes, onRateProviderToggle],
  );

  const handlePopularFilterClick = useCallback<
    (popularFilter: {
      id: number;
      name: string;
      count: number;
      type: 'rateAmenity' | 'todayDeals';
    }) => void
  >(
    (popularFilter) => {
      if (popularFilter.type === 'rateAmenity') {
        const filterName = popularFilter.name.replace(' ', '_').toLowerCase();
        handleRateAmenityClick(popularFilter.id);
        onPopularFilterClick(filterName);
      } else {
        onIsTodayDealsToggle(!isTodayDeals);
        onPopularFilterClick('todays_deals');
      }
    },
    [handleRateAmenityClick, onIsTodayDealsToggle, onPopularFilterClick],
  );

  const getReviewScoreTranslation = (reviewScore: number, isTo: boolean): string => {
    const scaleIndex = isTo ? [1, 2, 3, 4, 5, 6] : [0, 1, 2, 3, 4, 5];
    const newScaleLabels = ['poor', 'fair', 'pleasant', 'good', 'verygood', 'excellent'];
    const oldScaleLabels = ['fair', 'good', 'verygood', 'superb', 'excellent', ''];

    for (let i = 0; i < scaleIndex.length; i++) {
      if (reviewScore === reviewScoresRange[scaleIndex[i]]) {
        const scale = isNewRatingScale ? newScaleLabels[i] : oldScaleLabels[i];
        return scale ? (translations[`review_score_${scale}` as TranslationsKey] as string) : '';
      }
    }

    return '';
  };

  const getBookingOptionFilterTranslation = useCallback<(bookingOptionName: string) => string>(
    (bookingOptionName: string) => {
      switch (bookingOptionName) {
        case 'BOW':
          return translations.book_on_wego as string;
        case 'HOTEL_WEBSITE':
          return translations.official_hotel_sites as string;
        case 'TRAVEL_AGENCY':
          return translations.travel_agency_sites as string;
        default:
          return '';
      }
    },
    [translations],
  );

  return (
    <div className={clsx(styles.container, className)}>
      <div className={styles.resultsCountAndClearFilter}>
        {hotelsCount === 0 ? (
          <div className={styles.skeleton}></div>
        ) : (
          <>
            <div className={styles.resultsCount}>
              {translateText(
                translations.number_of_results,
                locale,
                filteredHotelsCount,
                hotelsCount,
              )}
            </div>
            <div className={styles.clearFilter} onClick={onClearFilter}>
              {translations.clear}
            </div>
          </>
        )}
      </div>

      {/* Skeleton */}
      {hotelsCount === 0
        ? [...Array(3).keys()].map((value: number) => {
            return (
              <React.Fragment key={value}>
                <div className={clsx(styles.filterCategory, value !== 0 && styles.withTopMargin)}>
                  <div className={styles.skeleton}></div>
                </div>

                <div className={styles.filterItem}>
                  <div className={clsx(styles.checkbox, styles.skeleton)}></div>

                  <div className={styles.labelAndCount}>
                    <div className={styles.skeleton}></div>
                  </div>
                </div>

                <div className={styles.filterItem}>
                  <div className={clsx(styles.checkbox, styles.skeleton)}></div>

                  <div className={styles.labelAndCount}>
                    <div className={clsx(styles.skeleton, styles.large)}></div>
                  </div>
                </div>

                <div className={styles.filterItem}>
                  <div className={clsx(styles.checkbox, styles.skeleton)}></div>

                  <div className={styles.labelAndCount}>
                    <div className={styles.skeleton}></div>
                  </div>
                </div>

                <div className={styles.filterItem}>
                  <div className={clsx(styles.checkbox, styles.skeleton)}></div>

                  <div className={styles.labelAndCount}>
                    <div className={clsx(styles.skeleton, styles.large)}></div>
                  </div>
                </div>
              </React.Fragment>
            );
          })
        : null}

      {hotelsCount > 0 ? (
        <>
          {/* Popular Filter */}
          {popularFilters.length > 0 ? (
            <>
              <div
                className={clsx(
                  styles.filterCategory,
                  isPopularFilterExpanded && styles.expanded,
                  (selectedRateAmenityIds.length > 0 || isTodayDeals) && styles.isFilteredBy,
                )}
                onClick={() => setIsPopularFilterExpanded(!isPopularFilterExpanded)}
              >
                {translations.popular_filters}

                <i className={clsx(styles.chevron, styles.bottom)}></i>
              </div>

              {isPopularFilterExpanded ? (
                <>
                  {popularFilters.map((popularFilter) => (
                    <div key={popularFilter.id} className={styles.filterItem}>
                      <Checkbox
                        className={styles.checkbox}
                        isChecked={
                          popularFilter.type === 'rateAmenity'
                            ? selectedRateAmenityIds.includes(popularFilter.id)
                            : isTodayDeals
                        }
                        onClick={() => handlePopularFilterClick(popularFilter)}
                      />

                      <div className={styles.labelAndCount}>
                        <div
                          className={styles.label}
                          title={popularFilter.name}
                          onClick={() => handlePopularFilterClick(popularFilter)}
                        >
                          {popularFilter.name}
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              ) : null}
            </>
          ) : null}

          {/* Price Filter */}
          {!!minUsdPrice && !!maxUsdPrice ? (
            <>
              <div
                className={clsx(
                  styles.filterCategory,
                  styles.withTopMargin,
                  isPriceFilterExpanded && styles.expanded,
                  !(selectedUsdPrices[0] === minUsdPrice && selectedUsdPrices[1] === maxUsdPrice) &&
                    styles.isFilteredBy,
                )}
                onClick={() => setIsPriceFilterExpanded(!isPriceFilterExpanded)}
              >
                {translations.price}

                <i className={clsx(styles.chevron, styles.bottom)}></i>
              </div>

              {isPriceFilterExpanded ? (
                <>
                  <div className={styles.filterItem}>
                    <Checkbox
                      className={styles.checkbox}
                      isChecked={priceOptions.isIncludingTaxOrFee}
                      onClick={() => onIsIncludingTaxChanged(!priceOptions.isIncludingTaxOrFee)}
                    />

                    <div className={styles.labelAndCount}>
                      <div
                        className={styles.label}
                        title={translations.include_tax as string}
                        onClick={() => onIsIncludingTaxChanged(!priceOptions.isIncludingTaxOrFee)}
                      >
                        {translations.include_tax}
                      </div>
                    </div>
                  </div>

                  <div className={styles.sliderContainer}>
                    <div className={styles.range}>
                      <Price
                        isInline
                        className={styles.price}
                        price={{
                          amount: newSelectedUsdPrices[0] ?? minUsdPrice,
                          amountUsd: newSelectedUsdPrices[0] ?? minUsdPrice,
                          currencyCode: 'USD',
                        }}
                        locale={locale}
                        currency={currency}
                        exchangeRate={exchangeRate}
                      />
                      <Price
                        isInline
                        className={styles.price}
                        price={{
                          amount: newSelectedUsdPrices[1] ?? maxUsdPrice,
                          amountUsd: newSelectedUsdPrices[1] ?? maxUsdPrice,
                          currencyCode: 'USD',
                        }}
                        locale={locale}
                        currency={currency}
                        exchangeRate={exchangeRate}
                      />
                    </div>

                    <LogSlider
                      isRtl={isRtl}
                      max={maxUsdPrice}
                      min={minUsdPrice}
                      onChange={handlePriceSliderChange}
                      onChangeCommitted={handlePriceSliderChangeCommitted}
                      value={newSelectedUsdPrices}
                    />
                  </div>

                  <button
                    className={clsx(
                      styles.priceType,
                      isPriceTypeDropdownExpanded && styles.expanded,
                    )}
                    onClick={() => setIsPriceTypeDropdownExpanded(!isPriceTypeDropdownExpanded)}
                  >
                    <div className={styles.labelsContainer}>
                      <div className={styles.priceTypeLabel}>
                        {priceOptions.showTotalPrice
                          ? translations.total_stay
                          : translations.hotel_per_night}
                      </div>
                      <div className={styles.isIncludingTaxLabel}>
                        {priceOptions.isIncludingTaxOrFee
                          ? translations.include_tax
                          : translations.exclude_tax}
                      </div>
                    </div>
                    <i className={clsx(styles.triangle, styles.down)}></i>

                    {/* Price type dropdown */}
                    {isPriceTypeDropdownExpanded ? (
                      <div className={styles.dropdown} onClick={(e) => e.stopPropagation()}>
                        <div
                          className={clsx(
                            styles.item,
                            !priceOptions.showTotalPrice && styles.active,
                          )}
                          onClick={() => handlePriceTypeChange(false)}
                        >
                          {translations.hotel_per_night}
                        </div>

                        <div
                          className={clsx(
                            styles.item,
                            priceOptions.showTotalPrice && styles.active,
                          )}
                          onClick={() => handlePriceTypeChange(true)}
                        >
                          {translations.total_stay}
                        </div>
                      </div>
                    ) : null}
                  </button>
                </>
              ) : null}
            </>
          ) : null}

          {/* Stars Filter */}
          {stars.length > 0 ? (
            <>
              <div
                className={clsx(
                  styles.filterCategory,
                  styles.withTopMargin,
                  isStarsFilterExpanded && styles.expanded,
                  selectedStarIds.length > 0 && styles.isFilteredBy,
                )}
                onClick={() => setIsStarsFilterExpanded(!isStarsFilterExpanded)}
              >
                {translations.stars}

                <i className={clsx(styles.chevron, styles.bottom)}></i>
              </div>

              {isStarsFilterExpanded ? (
                <>
                  <div className={styles.stars}>
                    {stars.map((star) => (
                      <div
                        key={star.id}
                        className={clsx(
                          styles.star,
                          selectedStarIds.includes(star.id) && styles.selected,
                        )}
                        onClick={() => handleStarClick(star.id)}
                      >
                        {translateNumber(star.name, locale === 'fa')}
                        <StarIcon className={styles.icon} />
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
            </>
          ) : null}

          {/* Review Score Filter */}
          {reviewScores.length > 0 ? (
            <>
              <div
                className={clsx(
                  styles.filterCategory,
                  styles.withTopMargin,
                  isReviewScoreFilterExpanded && styles.expanded,
                  !(
                    selectedReviewScoreSliderMark[0] === reviewScoreSliderMarks[0] &&
                    selectedReviewScoreSliderMark[1] ===
                      reviewScoreSliderMarks[reviewScoreSliderMarks.length - 1]
                  ) && styles.isFilteredBy,
                )}
                onClick={() => setIsReviewScoreFilterExpanded(!isReviewScoreFilterExpanded)}
              >
                {translations.review_score}

                <i className={clsx(styles.chevron, styles.bottom)}></i>
              </div>

              {isReviewScoreFilterExpanded ? (
                <div className={styles.sliderContainer}>
                  <div className={styles.range}>
                    <div className={styles.label}>
                      {getReviewScoreTranslation(
                        getReviewScoresRangeValue(selectedReviewScoreSliderMark[0]),
                        false,
                      )}
                    </div>
                    <div className={styles.label}>
                      {getReviewScoreTranslation(
                        getReviewScoresRangeValue(selectedReviewScoreSliderMark[1]),
                        true,
                      )}
                    </div>
                  </div>

                  <Slider
                    isRtl={isRtl}
                    marks={reviewScoreSliderMarks}
                    max={100}
                    min={0}
                    onChange={handleReviewScoreSliderChange}
                    onChangeCommitted={handleReviewScoreSliderChangeCommitted}
                    step={null}
                    value={selectedReviewScoreSliderMark}
                  />
                </div>
              ) : null}
            </>
          ) : null}

          {/* Booking Options Filter */}
          {bookingOptions.length > 0 ? (
            <>
              <div
                className={clsx(
                  styles.filterCategory,
                  styles.withTopMargin,
                  isBookingOptionsFilterExpanded && styles.expanded,
                  selectedBookingOptions.length > 0 && styles.isFilteredBy,
                )}
                onClick={() => setIsBookingOptionsFilterExpanded(!isBookingOptionsFilterExpanded)}
              >
                {translations.booking_options}

                <i className={clsx(styles.chevron, styles.bottom)}></i>
              </div>

              {isBookingOptionsFilterExpanded ? (
                <>
                  {bookingOptions.map((bookingOption) => (
                    <div key={bookingOption.id} className={styles.filterItem}>
                      <Checkbox
                        className={styles.checkbox}
                        isChecked={selectedBookingOptions.includes(
                          bookingOption.name === 'BOW'
                            ? 'wego'
                            : bookingOption.name === 'HOTEL_WEBSITE'
                            ? 'hotels'
                            : 'ota',
                        )}
                        onClick={() => handleBookingOptionClick(bookingOption.name)}
                      />

                      <div className={styles.labelAndCount}>
                        <div
                          className={styles.label}
                          title={getBookingOptionFilterTranslation(bookingOption.name)}
                          onClick={() => handleBookingOptionClick(bookingOption.name)}
                        >
                          {getBookingOptionFilterTranslation(bookingOption.name)}
                        </div>

                        <div className={styles.count}>{translateNumber(bookingOption.count)}</div>
                      </div>
                    </div>
                  ))}
                </>
              ) : null}
            </>
          ) : null}

          {/* Reviewer Groups Filter */}
          {reviewerGroups.length > 0 ? (
            <>
              <div
                className={clsx(
                  styles.filterCategory,
                  styles.withTopMargin,
                  isReviewerGroupsFilterExpanded && styles.expanded,
                  selectedReviewerGroups.length > 0 && styles.isFilteredBy,
                )}
                onClick={() => setIsReviewerGroupsFilterExpanded(!isReviewerGroupsFilterExpanded)}
              >
                {translations.guests_rate}

                <i className={clsx(styles.chevron, styles.bottom)}></i>
              </div>

              {isReviewerGroupsFilterExpanded ? (
                <>
                  {reviewerGroups.map((reviewerGroup) => (
                    <div key={reviewerGroup.id} className={styles.filterItem}>
                      <Checkbox
                        className={styles.checkbox}
                        isChecked={selectedReviewerGroups.includes(reviewerGroup.name)}
                        onClick={() => handleReviewerGroupClick(reviewerGroup.name)}
                      />

                      {/* <i className={clsx(styles.hotelAmenitiesIcon, styles[`amenity-${reviewerGroup.id}`])} onClick={() => handleAmenityClick(reviewerGroup.id)}></i> */}
                      {reviewerGroup.name === 'ALL' ? (
                        <WorldIcon
                          className={styles.reviewerGroupsIcon}
                          onClick={() => handleReviewerGroupClick(reviewerGroup.name)}
                        />
                      ) : null}
                      {reviewerGroup.name === 'BUSINESS' ? (
                        <BusinessIcon
                          className={styles.reviewerGroupsIcon}
                          onClick={() => handleReviewerGroupClick(reviewerGroup.name)}
                        />
                      ) : null}
                      {reviewerGroup.name === 'COUPLE' ? (
                        <CoupleIcon
                          className={styles.reviewerGroupsIcon}
                          onClick={() => handleReviewerGroupClick(reviewerGroup.name)}
                        />
                      ) : null}
                      {reviewerGroup.name === 'FAMILY' ? (
                        <FamilyIcon
                          className={styles.reviewerGroupsIcon}
                          onClick={() => handleReviewerGroupClick(reviewerGroup.name)}
                        />
                      ) : null}
                      {reviewerGroup.name === 'SOLO' ? (
                        <SoloIcon
                          className={styles.reviewerGroupsIcon}
                          onClick={() => handleReviewerGroupClick(reviewerGroup.name)}
                        />
                      ) : null}

                      <div className={clsx(styles.labelAndCount, styles.withIcon)}>
                        <div
                          className={styles.label}
                          title={getReviewerGroupFilterTranslation(
                            translations,
                            reviewerGroup.name,
                          )}
                          onClick={() => handleReviewerGroupClick(reviewerGroup.name)}
                        >
                          {getReviewerGroupFilterTranslation(translations, reviewerGroup.name)}
                        </div>

                        <div className={styles.count}>{translateNumber(reviewerGroup.count)}</div>
                      </div>
                    </div>
                  ))}
                </>
              ) : null}
            </>
          ) : null}

          {/* Rate Types & Inclusions Filter */}
          {rateAmenities.length > 0 ? (
            <>
              <div
                className={clsx(
                  styles.filterCategory,
                  styles.withTopMargin,
                  isRateAmenitiesFilterExpanded && styles.expanded,
                  selectedRateAmenityIds.length > 0 && styles.isFilteredBy,
                )}
                onClick={() => setIsRateAmenitiesFilterExpanded(!isRateAmenitiesFilterExpanded)}
              >
                {translations.rate_types}

                <i className={clsx(styles.chevron, styles.bottom)}></i>
              </div>

              {isRateAmenitiesFilterExpanded ? (
                <>
                  {rateAmenities.map((rateAmenity) => (
                    <div key={rateAmenity.id} className={styles.filterItem}>
                      <Checkbox
                        className={styles.checkbox}
                        isChecked={selectedRateAmenityIds.includes(rateAmenity.id)}
                        onClick={() => handleRateAmenityClick(rateAmenity.id)}
                      />

                      <div className={styles.labelAndCount}>
                        <div
                          className={styles.label}
                          title={rateAmenity.name}
                          onClick={() => handleRateAmenityClick(rateAmenity.id)}
                        >
                          {rateAmenity.name}
                        </div>

                        <div className={styles.count}>{translateNumber(rateAmenity.count)}</div>
                      </div>
                    </div>
                  ))}
                </>
              ) : null}
            </>
          ) : null}

          {/* Accommodation Types Filter */}
          {propertyTypes.length > 0 ? (
            <>
              <div
                className={clsx(
                  styles.filterCategory,
                  styles.withTopMargin,
                  isPropertyTypesFilterExpanded && styles.expanded,
                  selectedPropertyTypeIds.length > 0 && styles.isFilteredBy,
                )}
                onClick={() => setIsPropertyTypesFilterExpanded(!isPropertyTypesFilterExpanded)}
              >
                {translations.accomodation_type}

                <i className={clsx(styles.chevron, styles.bottom)}></i>
              </div>

              {isPropertyTypesFilterExpanded ? (
                <>
                  {propertyTypes.map((propertyType) => (
                    <div key={propertyType.id} className={styles.filterItem}>
                      <Checkbox
                        className={styles.checkbox}
                        isChecked={selectedPropertyTypeIds.includes(propertyType.id)}
                        onClick={() => handlePropertyTypeClick(propertyType.id)}
                      />

                      <div className={styles.labelAndCount}>
                        <div
                          className={styles.label}
                          title={propertyType.name}
                          onClick={() => handlePropertyTypeClick(propertyType.id)}
                        >
                          {propertyType.name}
                        </div>

                        <div className={styles.count}>{translateNumber(propertyType.count)}</div>
                      </div>
                    </div>
                  ))}
                </>
              ) : null}
            </>
          ) : null}

          {/* Districts or Areas Filter */}
          {districts.length > 0 ? (
            <>
              <div
                className={clsx(
                  styles.filterCategory,
                  styles.withTopMargin,
                  isDistrictsFilterExpanded && styles.expanded,
                  selectedDistrictIds.length > 0 && styles.isFilteredBy,
                )}
                onClick={() => setIsDistrictsFilterExpanded(!isDistrictsFilterExpanded)}
              >
                {translations.hotel_filter_districts}

                <i className={clsx(styles.chevron, styles.bottom)}></i>
              </div>

              {isDistrictsFilterExpanded ? (
                <>
                  {districts.map((district) => (
                    <div key={district.id} className={styles.filterItem}>
                      <Checkbox
                        className={styles.checkbox}
                        isChecked={selectedDistrictIds.includes(district.id)}
                        onClick={() => handleDistrictClick(district.id)}
                      />

                      <div className={styles.labelAndCount}>
                        <div
                          className={styles.label}
                          title={district.name}
                          onClick={() => handleDistrictClick(district.id)}
                        >
                          {district.name}
                        </div>

                        <div className={styles.count}>{translateNumber(district.count)}</div>
                      </div>
                    </div>
                  ))}
                </>
              ) : null}
            </>
          ) : null}

          {/* Hotel Amenities Filter */}
          {amenities.length > 0 ? (
            <>
              <div
                className={clsx(
                  styles.filterCategory,
                  styles.withTopMargin,
                  isAmenitiesFilterExpanded && styles.expanded,
                  selectedAmenityIds.length > 0 && styles.isFilteredBy,
                )}
                onClick={() => setIsAmenitiesFilterExpanded(!isAmenitiesFilterExpanded)}
              >
                {translations.hotel_amenities}

                <i className={clsx(styles.chevron, styles.bottom)}></i>
              </div>

              {isAmenitiesFilterExpanded ? (
                <>
                  {amenities.slice(0, 10).map((amenity) => {
                    return (
                      <div key={amenity.id} className={styles.filterItem}>
                        <Checkbox
                          className={styles.checkbox}
                          isChecked={selectedAmenityIds.includes(amenity.id)}
                          onClick={() => handleAmenityClick(amenity.id)}
                        />
                        <HotelAmenityIcon
                          amenityId={amenity.id}
                          className={styles.hotelAmenitiesIcon}
                          onClick={() => handleAmenityClick(amenity.id)}
                        />
                        <div className={clsx(styles.labelAndCount, styles.withIcon)}>
                          <div
                            className={styles.label}
                            title={amenity.name}
                            onClick={() => handleAmenityClick(amenity.id)}
                          >
                            {amenity.name}
                          </div>

                          <div className={styles.count}>{translateNumber(amenity.count)}</div>
                        </div>
                      </div>
                    );
                  })}
                </>
              ) : null}
            </>
          ) : null}

          {/* Chains Filter */}
          {chains.length > 0 ? (
            <>
              <div
                className={clsx(
                  styles.filterCategory,
                  styles.withTopMargin,
                  isChainsFilterExpanded && styles.expanded,
                  selectedChainIds.length > 0 && styles.isFilteredBy,
                )}
                onClick={() => setIsChainsFilterExpanded(!isChainsFilterExpanded)}
              >
                {translations.chains}

                <i className={clsx(styles.chevron, styles.bottom)}></i>
              </div>

              {isChainsFilterExpanded ? (
                <>
                  {chains.map((chain) => (
                    <div key={chain.id} className={styles.filterItem}>
                      <Checkbox
                        className={styles.checkbox}
                        isChecked={selectedChainIds.includes(chain.id)}
                        onClick={() => handleChainClick(chain.id)}
                      />

                      <div className={styles.labelAndCount}>
                        <div
                          className={styles.label}
                          title={chain.name}
                          onClick={() => handleChainClick(chain.id)}
                        >
                          {chain.name}
                        </div>

                        <div className={styles.count}>{translateNumber(chain.count)}</div>
                      </div>
                    </div>
                  ))}
                </>
              ) : null}
            </>
          ) : null}

          {/* Brands Filter */}
          {brands.length > 0 ? (
            <>
              <div
                className={clsx(
                  styles.filterCategory,
                  styles.withTopMargin,
                  isBrandsFilterExpanded && styles.expanded,
                  selectedBrandIds.length > 0 && styles.isFilteredBy,
                )}
                onClick={() => setIsBrandsFilterExpanded(!isBrandsFilterExpanded)}
              >
                {translations.brands}

                <i className={clsx(styles.chevron, styles.bottom)}></i>
              </div>

              {isBrandsFilterExpanded ? (
                <>
                  {brands.map((brand) => (
                    <div key={brand.id} className={styles.filterItem}>
                      <Checkbox
                        className={styles.checkbox}
                        isChecked={selectedBrandIds.includes(brand.id)}
                        onClick={() => handleBrandClick(brand.id)}
                      />

                      <div className={styles.labelAndCount}>
                        <div
                          className={styles.label}
                          title={brand.name}
                          onClick={() => handleBrandClick(brand.id)}
                        >
                          {brand.name}
                        </div>

                        <div className={styles.count}>{translateNumber(brand.count)}</div>
                      </div>
                    </div>
                  ))}
                </>
              ) : null}
            </>
          ) : null}

          {/* Booking Sites Filter */}
          {bookingSites.length > 0 ? (
            <>
              <div
                className={clsx(
                  styles.filterCategory,
                  styles.withTopMargin,
                  isBookingSitesFilterExpanded && styles.expanded,
                  selectedRateProviderCodes.length > 0 && styles.isFilteredBy,
                )}
                onClick={() => setIsBookingSitesFilterExpanded(!isBookingSitesFilterExpanded)}
              >
                {translations.booking_sites}

                <i className={clsx(styles.chevron, styles.bottom)}></i>
              </div>

              {isBookingSitesFilterExpanded ? (
                <>
                  {bookingSites.map((bookingSite) => (
                    <div key={bookingSite.code} className={styles.filterItem}>
                      <Checkbox
                        className={styles.checkbox}
                        isChecked={selectedRateProviderCodes.includes(bookingSite.code)}
                        onClick={() => handleRateProviderClick(bookingSite.code)}
                      />

                      <div className={styles.labelAndCount}>
                        <div
                          className={styles.label}
                          title={bookingSite.name}
                          onClick={() => handleRateProviderClick(bookingSite.code)}
                        >
                          {bookingSite.name}
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              ) : null}
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default HotelFilter;
